import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { IoRefreshOutline } from 'react-icons/io5';
import MarketTab from './MarketTab';
import MyCustomerTab from './MyCustomerTab';
import CompletedTab from './CompletedTab';

const TABS = [
  { key: 'market', label: '마켓', title: 'DB관리 - 마켓', subtitle: '마켓에서 원하는만큼 구매하세요' },
  { key: 'mine', label: '내 고객', title: 'DB관리 - 내 고객관리', subtitle: '' },
  { key: 'completed', label: '완료', title: 'DB관리 - 완료', subtitle: '서비스 완료·취소된 이력을 확인하실 수 있습니다' },
];

const DbManagePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TABS.find((t) => t.key === searchParams.get('tab'))?.key || 'market';
  const [tab, setTab] = useState(initialTab);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const current = TABS.find((t) => t.key === tab) || TABS[0];

  const handleTabChange = (key) => {
    setTab(key);
    setSearchParams({ tab: key }, { replace: true });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    await new Promise((r) => setTimeout(r, 600));
    setRefreshing(false);
  };

  return (
    <Wrap>
      <PageHeader>
        <PageTitleBox>
          <PageTitle>{current.title}</PageTitle>
          {current.subtitle && <PageSubtitle>{current.subtitle}</PageSubtitle>}
        </PageTitleBox>
      </PageHeader>

      <TabBarRow>
        <TabBtnGroup>
          {TABS.map((t) => (
            <TabBtn key={t.key} type="button" $active={tab === t.key} onClick={() => handleTabChange(t.key)}>
              {t.label}
            </TabBtn>
          ))}
        </TabBtnGroup>
        <RefreshBtn type="button" onClick={handleRefresh} disabled={refreshing}>
          <IoRefreshOutline size={14} />
        </RefreshBtn>
      </TabBarRow>

      {tab === 'market' && <MarketTab key={`market-${refreshKey}`} />}
      {tab === 'mine' && <MyCustomerTab key={`mine-${refreshKey}`} />}
      {tab === 'completed' && <CompletedTab key={`completed-${refreshKey}`} />}
    </Wrap>
  );
};

export default DbManagePage;

// ───── 스타일 ─────
const Wrap = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    max-width: 720px;
    margin: 0 auto;
    width: 100%;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.base};

  @media (min-width: 1024px) {
    margin-bottom: 20px;
  }
`;

const PageTitleBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PageSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;

  @media (min-width: 1024px) {
    font-size: 22px;
  }
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const TabBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TabBtnGroup = styled.div`
  display: flex;
  flex: 1;
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 3px;
`;

const TabBtn = styled.button`
  flex: 1;
  padding: 10px 0;
  font-size: 14px;
  font-weight: ${({ $active, theme }) => $active ? theme.fontWeight.bold : theme.fontWeight.medium};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  background: ${({ $active }) => $active ? '#fff' : 'transparent'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'};
  transition: all 0.15s;
`;
